import { z } from 'zod';

/**
 * Centro notifiche tenant (`/api/notifications/*`, campanella + badge mobile).
 * Contratto di risposta condiviso BE↔FE: il frontend NON ridichiara queste
 * shape (prima erano `interface` locali in NotificationBell + letture `any`
 * da `fetch` diretto).
 *
 *  - `GET /notifications?limit=`           → `data: NotificationListData`
 *  - `GET /notifications/unread-count`     → `data: NotificationUnreadCount`
 *  - `PATCH /notifications/:id/acknowledge`→ `data: NotificationAcknowledgeData`
 *  - `PATCH /notifications/:id/resolve`    → `data: NotificationResolveData`
 */

export const notificationPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export type NotificationPriority = z.infer<typeof notificationPrioritySchema>;

/** Metadati liberi per canale/evento: chiavi note tipizzate, resto passthrough. */
export const notificationMetadataSchema = z
  .object({
    channel: z.string().optional(),
    event_type: z.string().optional(),
    caller_number: z.string().optional(),
    caller_name: z.string().optional(),
    destination_number: z.string().optional(),
    extension_number: z.string().optional(),
    duration: z.number().optional(),
    call_time: z.string().optional(),
    from_email: z.string().optional(),
    from_name: z.string().optional(),
    subject: z.string().optional(),
    from_extension: z.string().optional(),
    to_extension: z.string().optional(),
    transfer_type: z.string().optional(),
    queue_name: z.string().optional(),
    wait_time: z.number().optional(),
    sender_number: z.string().optional(),
    sender_name: z.string().optional(),
    preview: z.string().optional(),
  })
  .passthrough();
export type NotificationMetadata = z.infer<typeof notificationMetadataSchema>;

export const tenantNotificationSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    reference_type: z.string().nullable(),
    reference_id: z.string().nullable(),
    read: z.boolean(),
    read_at: z.string().nullable(),
    /** Utente ha preso in carico la pratica (notifiche operative). */
    acknowledged_at: z.string().nullable(),
    /** Dominio ha chiuso la pratica (auto-resolve o manuale). */
    resolved_at: z.string().nullable(),
    priority: notificationPrioritySchema,
    action_url: z.string().nullable(),
    metadata: notificationMetadataSchema.nullable(),
    created_at: z.string(),
  })
  .passthrough();
export type TenantNotification = z.infer<typeof tenantNotificationSchema>;

export const notificationListDataSchema = z.object({
  notifications: z.array(tenantNotificationSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  has_more: z.boolean(),
});
export type NotificationListData = z.infer<typeof notificationListDataSchema>;

export const notificationUnreadCountSchema = z.object({
  count: z.number(),
  cached: z.boolean().optional(),
});
export type NotificationUnreadCount = z.infer<typeof notificationUnreadCountSchema>;

export const notificationAcknowledgeDataSchema = z
  .object({ acknowledged_at: z.string().nullable() })
  .passthrough();
export type NotificationAcknowledgeData = z.infer<typeof notificationAcknowledgeDataSchema>;

export const notificationResolveDataSchema = z
  .object({ resolved_at: z.string().nullable() })
  .passthrough();
export type NotificationResolveData = z.infer<typeof notificationResolveDataSchema>;

/** Contatore generico `{ count }` (`/messenger/unread-count`). */
export const countDataSchema = z.object({ count: z.number() });
export type CountData = z.infer<typeof countDataSchema>;
